import React, { useContext, useEffect } from 'react';

import { FinancialInformation, Sifarnik } from "../../model/FinancialInformationType";
import { useNavigate } from "react-router-dom";
import { ZahtjevContext } from "../../common/context/ZahtjevProvider";
import { CreateZahtjevFinancijskiPodaciRequest, useUpdateFinanceData } from "../../api/RegistrationApi";
import { Formik, FormikErrors, FormikValues, useFormikContext } from "formik";
import AutocompleteField from "../common/AutocompleteField";
import { Button } from "../common/Button";
import { validateField, validateForm } from "./financialInformationValidation";
import { ValidateOnMount } from "../common/ValidateOnMount";
import { useNotification } from "../../common/context/NotificationProvider";
import { Card } from "../common/Card";
import { ERROR_MESSAGE_NOTIFICATION } from "../../common/constants/notificationConstants";
import { FINANCIAL_INFORMATION_STATUS } from "../../common/constants/navigationConstants";
import {
    COUNTRIES_DATA,
    SIFARNIK_GOTOVINSKE_TRANSAKCIJE,
    SIFARNIK_IZVORA_OSTALIH_PRIHODA,
    SIFARNIK_OCEKIVANI_PROMET,
    SIFARNIK_PRILJEVA_SREDSTAVA,
    SIFARNIK_PROSJECAN_IZNOS_OSTALIH_PRIHODA,
    SIFARNIK_PROSJECNA_PRIMANJA,
    SIFARNIK_STATUS_ZAPOSLENJA,
    SIFARNIK_SVRHE_RACUNA,
    SIFARNIK_VRSTA_POSLODAVCA,
    SIFARNIK_VRSTA_ZAPOSLJENJA,
    SIFARNIK_VRSTE_TRANSAKCIJA
} from "../../common/data/sifarnik";
import { DropDownMultipleField } from "../common/DropDownMultipleField";
import { toNullOrString } from "../../util/stringUtil";
import { Alert, Dialog, DialogActions, DialogContent, DialogContentText } from "@mui/material";
import { useMediaQuery } from "react-responsive";
import { Trans, useTranslation } from "react-i18next";
import { SupportedLanguage } from "../../i18n";
import { scrollToFirstError } from "../../util/formUtil";

const GOTOVINSKA_TRANSAKCIJA_ID = "1";
const VRSTA_TRANSAKCIJA_PREKOGRANICNO = "3";
const SVRHA_RACUNA_PREKOGRANICNO = "4";
const NEMA_PRIHODA_ID = "6";
const STATUS_ZAPOSLENJA_NEZAPOSLEN_ID = "U";
const STATUS_ZAPOSLENJA_STUDENT_ID = "O";
const STATUS_ZAPOSLENJA_UMIROVLJENIK_ID = "R";
const STATUS_ZAPOSLENJA_UCENIK_ID = "H";
const INITIAL_FINANCIAL_INFORMATION_FORMIK_VALUES: FinancialInformation = {} as FinancialInformation;

const translateFinancialInfoVrijednost = (data: FinancialInformation, t: (key: string) => string): FinancialInformation => {
    if (!data || Object.keys(data).length === 0) {
        return data;
    }

    return {
        ...data,
        statusZaposlenja: data.statusZaposlenja ? {
            ...data.statusZaposlenja,
            vrijednost: t(data.statusZaposlenja.vrijednost)
        } : data.statusZaposlenja,
        vrstaPoslodavca: data.vrstaPoslodavca ? {
            ...data.vrstaPoslodavca,
            vrijednost: t(data.vrstaPoslodavca.vrijednost)
        } : undefined,
        prosjecnaMjesecnaPrimanjaRedovna: data.prosjecnaMjesecnaPrimanjaRedovna ? {
            ...data.prosjecnaMjesecnaPrimanjaRedovna,
            vrijednost: t(data.prosjecnaMjesecnaPrimanjaRedovna.vrijednost)
        } : data.prosjecnaMjesecnaPrimanjaRedovna,
        prosjecanIznosOstalihPrihoda: data.prosjecanIznosOstalihPrihoda ? {
            ...data.prosjecanIznosOstalihPrihoda,
            vrijednost: t(data.prosjecanIznosOstalihPrihoda.vrijednost)
        } : undefined,
        pozicijaZaposlenja: data.pozicijaZaposlenja ? {
            ...data.pozicijaZaposlenja,
            vrijednost: t(data.pozicijaZaposlenja.vrijednost)
        } : undefined,
        svrheOtvaranjaRacuna: data.svrheOtvaranjaRacuna?.map(item => ({ ...item, vrijednost: t(item.vrijednost) })),
        izvoriOstalihPrihoda: data.izvoriOstalihPrihoda?.map(item => ({ ...item, vrijednost: t(item.vrijednost) })),
        vrsteTransakcijaNaRacunu: data.vrsteTransakcijaNaRacunu?.map(item => ({
            ...item,
            vrijednost: t(item.vrijednost)
        })),
        prosjecneGotovinskeTransakcije: data.prosjecneGotovinskeTransakcije ? {
            ...data.prosjecneGotovinskeTransakcije,
            vrijednost: t(data.prosjecneGotovinskeTransakcije.vrijednost)
        } : undefined,
        ocekivaniGodisnjiPromet: data.ocekivaniGodisnjiPromet ? {
            ...data.ocekivaniGodisnjiPromet,
            vrijednost: t(data.ocekivaniGodisnjiPromet.vrijednost)
        } : data.ocekivaniGodisnjiPromet,
        osnovePriljevaSredstava: data.osnovePriljevaSredstava?.map(item => ({
            ...item,
            vrijednost: t(item.vrijednost)
        })),
    };
};

const GOTOVINSKE_TRANSAKCIJE_AVERAGE_VALUE = new Map<string, number>([
    ["1", 12000],
    ["2", 60000],
    ["3", 300000],
    ["4", 900000],
    ["5", 1200000],
]);

const PROSJECNA_PRIMANJA_AVERAGE_VALUE = new Map<string, number>([
    ["1", 36000],
    ["2", 80000],
    ["3", 150000],
]);

const OSTALI_PRIHODI_AVERAGE_VALUE = new Map<string, number>([
    ["1", 22000],
    ["2", 50000],
    ["3", 80000],
]);

export function isUserWorking(statusZaposlenja: string | undefined): boolean {
    if (!statusZaposlenja) {
        return false;
    }

    return !(STATUS_ZAPOSLENJA_NEZAPOSLEN_ID === statusZaposlenja ||
      STATUS_ZAPOSLENJA_STUDENT_ID === statusZaposlenja ||
      STATUS_ZAPOSLENJA_UMIROVLJENIK_ID === statusZaposlenja ||
      STATUS_ZAPOSLENJA_UCENIK_ID === statusZaposlenja);
}

export function userHasNoOtherIncome(izvoriOstalihPrihoda: Sifarnik[]): boolean {
    if (izvoriOstalihPrihoda) {
        return izvoriOstalihPrihoda.some((item) => item.id === '6');
    } else {
        return false;
    }
}

export function svrhaRacunaOrVrsteTransakcijaHasPrekogranicnoPlacanje(svrheOtvaranjaRacuna: Sifarnik[] | undefined, vrsteTransakcijaNaRacunu: Sifarnik[] | undefined) {
    return vrsteTransakcijaNaRacunu?.some(vrstaTransakcije => vrstaTransakcije.id === VRSTA_TRANSAKCIJA_PREKOGRANICNO) ||
      svrheOtvaranjaRacuna?.some(svrhaRacuna => svrhaRacuna.id === SVRHA_RACUNA_PREKOGRANICNO);
}

function mapFinancialInformationToCreateZahtjevFinancijskiPodaciRequest(input: FinancialInformation): CreateZahtjevFinancijskiPodaciRequest {
    return {
        statusZaposlenja: input.statusZaposlenja.id,
        vrstaPoslodavca: toNullOrString(input?.vrstaPoslodavca?.id),
        prosjecnaMjesecnaPrimanjaRedovna: input.prosjecnaMjesecnaPrimanjaRedovna.id,
        prosjecanIznosOstalihPrihoda: toNullOrString(input.prosjecanIznosOstalihPrihoda?.id),
        pozicijaZaposlenja: toNullOrString(input?.pozicijaZaposlenja?.id),
        svrhaOtvaranjaRacuna: input.svrheOtvaranjaRacuna.map(data => data.id).join(','),
        izvorOstalihPrihoda: input.izvoriOstalihPrihoda.map(data => data.id).join(','),
        vrsteTransakcijaNaRacunu: input.vrsteTransakcijaNaRacunu.map(data => data.id).join(','),
        prosjecneGotovinskeTransakcije: toNullOrString(input?.prosjecneGotovinskeTransakcije?.id),
        ocekivaniGodisnjiPromet: input.ocekivaniGodisnjiPromet.id,
        osnovaPriljevaSredstava: input.osnovePriljevaSredstava.map(data => data.id).join(','),
        drzavePrekogranicnoPlacanje: toNullOrString(input.drzavePrekogranicnoPlacanje?.map(data => data.oznaka).join(","))
    }
}

const FinancialThresholdEnforcer = () => {
    const { values, submitCount, setFieldError, setFieldTouched } = useFormikContext<FinancialInformation>();

    useEffect(() => {
        if (submitCount === 0) return;

        const selectedPrometId = values.ocekivaniGodisnjiPromet?.id;
        if (!selectedPrometId) return;

        const redovniPrihodi =
          PROSJECNA_PRIMANJA_AVERAGE_VALUE.get(values.prosjecnaMjesecnaPrimanjaRedovna?.id ?? "") ?? 0;
        const ostaliPrihodi =
          OSTALI_PRIHODI_AVERAGE_VALUE.get(values.prosjecanIznosOstalihPrihoda?.id ?? "") ?? 0;
        const gotovinskiPrihodi =
          GOTOVINSKE_TRANSAKCIJE_AVERAGE_VALUE.get(values.prosjecneGotovinskeTransakcije?.id ?? "") ?? 0;

        const total = redovniPrihodi + ostaliPrihodi + gotovinskiPrihodi;

        const requiredPrometId =
          total <= 70000 ? "1"
            : total <= 150000 ? "2"
              : total <= 500000 ? "3"
                : total <= 1000000 ? "4"
                  : "5";

        if (selectedPrometId !== requiredPrometId) {
            setFieldTouched("ocekivaniGodisnjiPromet", true, false);

            setFieldError("ocekivaniGodisnjiPromet", "financial-information-page.expected-annual-turnover-field.validation-error.general");
        } else {
            setFieldError("ocekivaniGodisnjiPromet", undefined);
        }
    }, [
        submitCount,
        values.prosjecnaMjesecnaPrimanjaRedovna?.id,
        values.prosjecanIznosOstalihPrihoda?.id,
        values.prosjecneGotovinskeTransakcije?.id,
        values.ocekivaniGodisnjiPromet?.id,
        setFieldError,
        setFieldTouched,
    ]);

    return null;
};

const FinancialInformationPage = () => {
    const { t, i18n } = useTranslation();
    const lng = i18n.resolvedLanguage as SupportedLanguage;
    const {
        financialInformation,
        financialInformationCallback,
        getZahtjevId,
        restoreContext,
        getStatus
    } = useContext(ZahtjevContext);
    const { isPending: isLoading, mutate: updateFinanceDataMutation } = useUpdateFinanceData(getZahtjevId());
    const currentStatus = getStatus();
    const pageStatus = FINANCIAL_INFORMATION_STATUS;
    const { notify } = useNotification();
    const initalFinancialInformationFormValues = Object.keys(financialInformation).length !== 0
      ? translateFinancialInfoVrijednost(financialInformation, t)
      : INITIAL_FINANCIAL_INFORMATION_FORMIK_VALUES;
    const navigate = useNavigate();
    const isMobile = useMediaQuery({ maxWidth: 767 });

    type ThresholdDialogPayload = {
        total: number;
        requiredPrometId: string;
        selectedPrometId: string;
        direction: "HIGH" | "LOW";
    };

    const [thresholdDialog, setThresholdDialog] = React.useState<{
        open: boolean;
        payload?: ThresholdDialogPayload;
    }>({ open: false });

    const validateFinancialThresholds = (values: FinancialInformation, setFieldError: (field: string, message: (string | undefined)) => void) => {
        const selectedPrometId = values.ocekivaniGodisnjiPromet?.id;

        const redovniPrihodi =
          PROSJECNA_PRIMANJA_AVERAGE_VALUE.get(values.prosjecnaMjesecnaPrimanjaRedovna?.id ?? "") ?? 0;
        const ostaliPrihodi =
          OSTALI_PRIHODI_AVERAGE_VALUE.get(values.prosjecanIznosOstalihPrihoda?.id ?? "") ?? 0;
        const gotovinskiPrihodi =
          GOTOVINSKE_TRANSAKCIJE_AVERAGE_VALUE.get(values.prosjecneGotovinskeTransakcije?.id ?? "") ?? 0;

        const total = redovniPrihodi + ostaliPrihodi + gotovinskiPrihodi;

        const requiredPrometId =
          total <= 70000 ? "1"
            : total <= 150000 ? "2"
              : total <= 500000 ? "3"
                : total <= 1000000 ? "4"
                  : "5";

        if (!selectedPrometId) {
            return false;
        }

        if (selectedPrometId !== requiredPrometId) {
            setFieldError(
              "ocekivaniGodisnjiPromet",
              "financial-information-page.expected-annual-turnover-field.validation-error.general"
            );

            const direction = Number(selectedPrometId) < Number(requiredPrometId) ? "HIGH" : "LOW";

            setThresholdDialog({
                open: true,
                payload: { total, requiredPrometId, selectedPrometId, direction }
            });
            return false;
        } else {
            setFieldError("ocekivaniGodisnjiPromet", undefined);
            return true;
        }
    };

    const handleSubmit = (values: FinancialInformation, setFieldError: (field: string, message: (string | undefined)) => void) => {
        const isFormValid = validateForm(values, setFieldError);

        if (isFormValid && validateFinancialThresholds(values, setFieldError)) {
            const request = mapFinancialInformationToCreateZahtjevFinancijskiPodaciRequest(values);

            updateFinanceDataMutation(request, {
                onSuccess: () => {
                    financialInformationCallback(values);
                    navigate('/tax-residency');
                },
                onError: () => {
                    notify(ERROR_MESSAGE_NOTIFICATION, "error");
                }
            });
        } else {
            scrollToFirstError();
        }
    };

    const handleReturn = (values: FinancialInformation, dirty: boolean) => {
        if (dirty) financialInformationCallback(values);
        navigate('/contact-address', { state: { isBack: true } });
    }

    const handleValidation = (setFieldError: (field: string, message: (string | undefined)) => void, formValues?: FinancialInformation) =>
      (value: any, field?: string) => {
          if (field === "prosjecneGotovinskeTransakcije" && formValues && !formValues?.vrsteTransakcijaNaRacunu?.some(vrstaTransakcije => vrstaTransakcije.id === GOTOVINSKA_TRANSAKCIJA_ID)) {
              return true;
          }

          return validateField(field!, value, setFieldError);
      };

    const handleSetFieldValue = (setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>, formValues?: FinancialInformation, setFieldError?: (field: string, message: (string | undefined)) => void) =>
      (value: any, field?: string) => {
          const currentField = field as keyof FinancialInformation;

          if (currentField === "izvoriOstalihPrihoda") {
              const newValue = (value as Sifarnik[]);
              const addedValue = newValue.length > 0 ? newValue[newValue.length - 1] : null;

              if (addedValue && addedValue?.id === "6" && setFieldError && setFieldValue) {
                  setFieldValue("prosjecanIznosOstalihPrihoda", null, false);
                  setFieldError("prosjecanIznosOstalihPrihoda", undefined);
              }

              if (addedValue && (addedValue.id === NEMA_PRIHODA_ID || newValue.some(sifarnik => sifarnik.id === NEMA_PRIHODA_ID))) {
                  setFieldValue("izvoriOstalihPrihoda", [addedValue], false);
                  return;
              }
          } else if (currentField === "vrsteTransakcijaNaRacunu" && setFieldError && formValues) {
              const newValue = (value as Sifarnik[]);
              if (!svrhaRacunaOrVrsteTransakcijaHasPrekogranicnoPlacanje(formValues?.svrheOtvaranjaRacuna, newValue)) {
                  setFieldValue("drzavePrekogranicnoPlacanje", null, false);
                  setFieldError("drzavePrekogranicnoPlacanje", undefined);
              }

              const isValueBeingRemoved = formValues?.vrsteTransakcijaNaRacunu?.length && (formValues.vrsteTransakcijaNaRacunu.length > (value as Sifarnik[])?.length);
              if (isValueBeingRemoved) {
                  const removedValueId = formValues.vrsteTransakcijaNaRacunu.map(sifarnik => sifarnik.id).filter((sifarnikId) => !(value as Sifarnik[]).map(sifarnik => sifarnik.id).includes(sifarnikId))?.at(0);
                  if (removedValueId === GOTOVINSKA_TRANSAKCIJA_ID) {
                      setFieldValue("prosjecneGotovinskeTransakcije", null, false);
                      setFieldError("prosjecneGotovinskeTransakcije", undefined);
                  }
              }
          } else if (currentField === "statusZaposlenja" && setFieldError) {
              const userOldEmploymentStatusIsWorking = isUserWorking(formValues?.statusZaposlenja?.id);
              const userNewEmploymentStatusIsWorking = isUserWorking((value as Sifarnik)?.id);
              if (userOldEmploymentStatusIsWorking && !userNewEmploymentStatusIsWorking) {
                  setFieldValue("vrstaPoslodavca", null, false);
                  setFieldError("vrstaPoslodavca", undefined);
                  setFieldValue("pozicijaZaposlenja", null, false);
                  setFieldError("pozicijaZaposlenja", undefined);
              }
          } else if (currentField === "svrheOtvaranjaRacuna" && setFieldError) {
              const newValue = (value as Sifarnik[]);

              if (!svrhaRacunaOrVrsteTransakcijaHasPrekogranicnoPlacanje(newValue, formValues?.vrsteTransakcijaNaRacunu)) {
                  setFieldValue("drzavePrekogranicnoPlacanje", null, false);
                  setFieldError("drzavePrekogranicnoPlacanje", undefined);
              }
          }

          return setFieldValue(field!, value, false);
      };

    useEffect(() => {
        restoreContext(pageStatus);
    }, []);

    return (
      <Card title={t("financial-information-page.title")} isLoading={parseInt(currentStatus) < parseInt(pageStatus)}>
          <Formik
            initialValues={initalFinancialInformationFormValues}
            onSubmit={(values, { setFieldError }) => handleSubmit(values, setFieldError)}
          >
              {({ values, errors, handleSubmit, setFieldError, setFieldValue, dirty }) => (
                <form noValidate onSubmit={handleSubmit} autoComplete="off">
                    <ValidateOnMount shouldValidate={Object.keys(financialInformation).length !== 0} validate={validateForm} />
                    <FinancialThresholdEnforcer/>

                    <div className="space-y-5">
                        <Alert className="mb-2" sx={{ alignItems: 'center' }} severity="info">
                            {t("common.alert-required-fields")}
                        </Alert>
                        <AutocompleteField
                          name="statusZaposlenja"
                          label={t("financial-information-page.employment-status-field.label")}
                          options={SIFARNIK_STATUS_ZAPOSLENJA}
                          getOptionLabel={(option) => t(option.vrijednost)}
                          isOptionEqualToValue={(option, value) => option?.id === value?.id}
                          defaultValue={values.statusZaposlenja}
                          error={t(errors?.statusZaposlenja as string)}
                          handleValueChange={handleSetFieldValue(setFieldValue, values, setFieldError)}
                          handleValidation={handleValidation(setFieldError, values)}
                        />

                        {isUserWorking(values?.statusZaposlenja?.id) &&
                            <AutocompleteField
                                name="vrstaPoslodavca"
                                label={t("financial-information-page.employer-type-field.label")}
                                options={SIFARNIK_VRSTA_POSLODAVCA}
                                getOptionLabel={(option) => t(option?.vrijednost) || ''}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                defaultValue={values.vrstaPoslodavca}
                                error={t(errors?.vrstaPoslodavca as string)}
                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                handleValidation={handleValidation(setFieldError)}
                                info={t("financial-information-page.employer-type-field.info")}
                            />
                        }

                        {isUserWorking(values?.statusZaposlenja?.id) &&
                            <AutocompleteField
                                name="pozicijaZaposlenja"
                                label={t("financial-information-page.employment-position-field.label")}
                                options={SIFARNIK_VRSTA_ZAPOSLJENJA}
                                getOptionLabel={(option) => t(option?.vrijednost) || ''}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                defaultValue={values.pozicijaZaposlenja}
                                error={t(errors?.pozicijaZaposlenja as string)}
                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                handleValidation={handleValidation(setFieldError)}
                            />
                        }

                        <AutocompleteField
                          name="prosjecnaMjesecnaPrimanjaRedovna"
                          label={t("financial-information-page.average-monthly-income-field.label")}
                          options={SIFARNIK_PROSJECNA_PRIMANJA}
                          getOptionLabel={(option) => t(option.vrijednost)}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          defaultValue={values.prosjecnaMjesecnaPrimanjaRedovna}
                          error={t(errors?.prosjecnaMjesecnaPrimanjaRedovna as string)}
                          handleValueChange={handleSetFieldValue(setFieldValue)}
                          handleValidation={handleValidation(setFieldError)}
                          info={t("financial-information-page.average-monthly-income-field.info")}
                        />

                        <DropDownMultipleField
                          name="izvoriOstalihPrihoda"
                          label={t("financial-information-page.other-income-sources-field.label")}
                          options={SIFARNIK_IZVORA_OSTALIH_PRIHODA}
                          value={values.izvoriOstalihPrihoda}
                          error={t(errors?.izvoriOstalihPrihoda as string)}
                          getOptionLabel={(option) => t(option.vrijednost)}
                          getOptionValue={(option) => option.id}
                          handleValueChange={handleSetFieldValue(setFieldValue, values, setFieldError)}
                          handleValidation={handleValidation(setFieldError)}
                        />

                        {Array.isArray(values.izvoriOstalihPrihoda) &&
                          values.izvoriOstalihPrihoda.length > 0 &&
                          !values.izvoriOstalihPrihoda.some(
                            (item) => item.id === '6'
                          ) && (
                            <AutocompleteField
                              name="prosjecanIznosOstalihPrihoda"
                              label={t("financial-information-page.other-income-average-monthly-income-field.label")}
                              options={SIFARNIK_PROSJECAN_IZNOS_OSTALIH_PRIHODA}
                              getOptionLabel={(option) => t(option.vrijednost)}
                              isOptionEqualToValue={(option, value) => option.id === value.id}
                              defaultValue={values.prosjecanIznosOstalihPrihoda}
                              error={t(errors?.prosjecanIznosOstalihPrihoda as string)}
                              handleValueChange={handleSetFieldValue(setFieldValue)}
                              handleValidation={handleValidation(setFieldError)}
                            />
                          )}


                        <DropDownMultipleField
                          name="svrheOtvaranjaRacuna"
                          label={t("financial-information-page.account-opening-purposes-field.label")}
                          options={SIFARNIK_SVRHE_RACUNA}
                          value={values.svrheOtvaranjaRacuna}
                          error={t(errors?.svrheOtvaranjaRacuna as string)}
                          getOptionLabel={(option) => t(option.vrijednost)}
                          getOptionValue={(option) => option.id}
                          handleValueChange={handleSetFieldValue(setFieldValue, values, setFieldError)}
                          handleValidation={handleValidation(setFieldError)}
                        />

                        <DropDownMultipleField
                          name="vrsteTransakcijaNaRacunu"
                          label={t("financial-information-page.account-transaction-types-field.label")}
                          options={SIFARNIK_VRSTE_TRANSAKCIJA}
                          value={values.vrsteTransakcijaNaRacunu}
                          error={t(errors?.vrsteTransakcijaNaRacunu as string)}
                          getOptionLabel={(option) => t(option.vrijednost)}
                          getOptionValue={(option) => option.id}
                          handleValueChange={handleSetFieldValue(setFieldValue, values, setFieldError)}
                          handleValidation={handleValidation(setFieldError)}
                        />

                        {
                          (values.vrsteTransakcijaNaRacunu?.some(vrstaTransakcije => vrstaTransakcije.id === VRSTA_TRANSAKCIJA_PREKOGRANICNO) ||
                            values.svrheOtvaranjaRacuna?.some(svrhaRacuna => svrhaRacuna.id === SVRHA_RACUNA_PREKOGRANICNO)) &&
                            <DropDownMultipleField
                                name="drzavePrekogranicnoPlacanje"
                                label={t("financial-information-page.cross-border-payment-states-field.label")}
                                options={COUNTRIES_DATA(lng)}
                                value={values.drzavePrekogranicnoPlacanje}
                                error={t(errors?.drzavePrekogranicnoPlacanje as string)}
                                getOptionLabel={(option) => option.drzava}
                                getOptionValue={(option) => option.oznaka}
                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                handleValidation={handleValidation(setFieldError)}
                            />
                        }

                        {values.vrsteTransakcijaNaRacunu?.some(vrstaTransakcije => vrstaTransakcije.id === GOTOVINSKA_TRANSAKCIJA_ID) &&
                            <AutocompleteField
                                name="prosjecneGotovinskeTransakcije"
                                label={t("financial-information-page.average-cash-transactions-field.label")}
                                options={SIFARNIK_GOTOVINSKE_TRANSAKCIJE}
                                getOptionLabel={(option) => t(option.vrijednost)}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                defaultValue={values.prosjecneGotovinskeTransakcije}
                                error={t(errors?.prosjecneGotovinskeTransakcije as string)}
                                handleValueChange={handleSetFieldValue(setFieldValue)}
                                handleValidation={handleValidation(setFieldError)}
                                info={t("financial-information-page.average-cash-transactions-field.info")}
                            />
                        }

                        <AutocompleteField
                          name="ocekivaniGodisnjiPromet"
                          label={t("financial-information-page.expected-annual-turnover-field.label")}
                          options={SIFARNIK_OCEKIVANI_PROMET}
                          getOptionLabel={(option) => t(option.vrijednost)}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          defaultValue={values.ocekivaniGodisnjiPromet}
                          error={t(errors?.ocekivaniGodisnjiPromet as string)}
                          handleValueChange={handleSetFieldValue(setFieldValue)}
                          handleValidation={handleValidation(setFieldError)}
                        />


                        <DropDownMultipleField
                          name="osnovePriljevaSredstava"
                          label={t("financial-information-page.funds-inflow-basics-field.label")}
                          options={SIFARNIK_PRILJEVA_SREDSTAVA}
                          value={values.osnovePriljevaSredstava}
                          error={t(errors?.osnovePriljevaSredstava as string)}
                          getOptionLabel={(option) => t(option.vrijednost)}
                          getOptionValue={(option) => option.id}
                          handleValueChange={handleSetFieldValue(setFieldValue)}
                          handleValidation={handleValidation(setFieldError)}
                        />

                        <div className="mt-10 text-center md:text-left">
                            <Button
                              onClick={() => handleReturn(values, dirty)}
                              text={t("common.button-previous")}
                              variant="secondary"
                              disabled={false}
                              type="button"
                            />
                            <Button
                              type="submit"
                              text={t("common.button-next")}
                              disabled={isLoading}
                            />
                        </div>
                    </div>
                </form>
              )}
          </Formik>
          <Dialog
            PaperProps={{ sx: { p: 3, borderRadius: 4, minWidth: isMobile ? 180 : 280 } }}
            open={thresholdDialog.open}
            onClose={() => setThresholdDialog({ open: false })}
          >
              <DialogContent sx={{ pb: 2 }}>
                  <DialogContentText id="threshold-dialog-description">
                      <Trans
                        i18nKey="financial-information-page.expected-annual-turnover-field.validation-error.invalid-selection"
                        values={{ direction: t(`financial-information-page.expected-annual-turnover-field.validation-error.direction-${thresholdDialog.payload?.direction === "HIGH" ? "higher" : "lower"}`) }}
                        components={{ br: <br/> }}
                      />
                  </DialogContentText>
              </DialogContent>

              <DialogActions>
                  <Button
                    text={t("common.button-ok")}
                    type="button"
                    onClick={() => setThresholdDialog({ open: false })}
                  />
              </DialogActions>
          </Dialog>

      </Card>
    );
};

export default FinancialInformationPage;
